import { Panel } from "@flightpath/coreui";
import { observer } from "mobx-react";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import I18n from "../../../../../core/localization/I18n";
import { StakeholderProfilingType, StakeholderType } from "../../../../../enums";
import { useCurrentOrganisationId } from "../../../../../services/local/organisationContext/OrganisationContextModel";
import { StakeholderGapAnalysis } from "./components/StakeholderGapAnalysis/StakeholderGapAnalysis_view";
import { StakeholderHeatmap } from "./components/StakeholderHeatmap/StakeholderHeatmap_view";
import { TotalStakeholder } from "./components/TotalStakeholders/TotalStakeholder_view";
import { StakeholderProfileLinks } from "./StakeholderReporting_components";
import { StakeholderReportingModel } from "./StakeholderReporting_model";

export const StakeholderReporting: React.FC<any> = observer(() => {
  const organisationId = useCurrentOrganisationId();
  const { projectId, stakeholderType, profilingType } = useParams<{
    projectId: string;
    stakeholderType: string;
    profilingType: string;
  }>();
  const projId = parseInt(projectId);
  const navigate = useNavigate();
  const stakeholderTypeCasted: StakeholderType = useMemo(
    () => (stakeholderType ? parseInt(stakeholderType) : StakeholderType.INDIVIDUAL),
    [stakeholderType]
  );

  const profilingTypeCasted: StakeholderProfilingType = useMemo(
    () => (profilingType ? parseInt(profilingType) : StakeholderProfilingType.Commitment),
    [profilingType]
  );

  const [model, setModel] = useState(
    () => new StakeholderReportingModel(organisationId, projId, navigate, stakeholderTypeCasted, profilingTypeCasted)
  );

  useEffect(() => {
    model.onMount();
    return model.onUnmount;
  }, [model]);

  useEffect(() => {
    if (
      organisationId !== model.organisationId ||
      projId !== model.projectId ||
      stakeholderTypeCasted !== model.stakeholderType ||
      profilingTypeCasted !== model.profileType
    ) {
      setModel(
        new StakeholderReportingModel(organisationId, projId, navigate, stakeholderTypeCasted, profilingTypeCasted)
      );
    }
  }, [
    navigate,
    model.organisationId,
    model.projectId,
    model.stakeholderType,
    model.profileType,
    projId,
    organisationId,
    stakeholderTypeCasted,
    profilingTypeCasted
  ]);

  const baseUrl = `/organisations/${organisationId}/projects/${projectId}/stakeholders/visualisations`;

  const stakeholderProfileSelector: JSX.Element = (
    <h4>
      <StakeholderProfileLinks
        stakeholderType={model.stakeholderType}
        baseUrl={baseUrl}
        profileType={model.profileType}
      />
    </h4>
  );

  return (
    <>
      <div className="container-fluid">
        <div className="row pt-4 ">
          <div className="col-4">
            <TotalStakeholder
              isLoading={model.isLoading}
              data={model.stakeholders}
              profilingType={
                `${model.profileType as any}` === "0"
                  ? "0"
                  : (StakeholderProfilingType[model.profileType].toLowerCase() as any)
              }
            />
          </div>
          <div className="col-4">
            <StakeholderGapAnalysis
              isLoading={model.isLoading}
              data={model.stakeholders}
              profilingType={
                `${model.profileType as any}` === "0"
                  ? "0"
                  : (StakeholderProfilingType[model.profileType].toLowerCase() as any)
              }
            />
          </div>
        </div>
        <div className="row my-5">
          <div className="col">
            <Panel.Panel hasBorder hasBorderRadius background={Panel.PanelBackgrounds.BG_LIGHT} className="p-3 w-100">
              <div className="col-12">
                <h2>{I18n.t("visualisations.stakeholderMap")}</h2>
              </div>
              <div className="px-3">
                <StakeholderHeatmap
                  stakeholderProfileSelector={stakeholderProfileSelector}
                  stakeholderType={model.stakeholderType}
                  data={model.stakeholders}
                  profilingType={
                    `${model.profileType as any}` === "0"
                      ? "0"
                      : (StakeholderProfilingType[model.profileType].toLowerCase() as any)
                  }
                />
              </div>
            </Panel.Panel>
          </div>
        </div>
      </div>
    </>
  );
});
